// @ts-nocheck
import { baseApi } from './base.api';
import { API_VERSION, PATH_KNOWLEDGE_BASE_API } from '../constants/api-routes';
import { BaseQueryMeta } from '@reduxjs/toolkit/query';

interface KnowledgeBase {
  id: number;
  account_id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  projects?: {
    id: number;
  }[];
}

interface KnowledgeBaseRequest {
  load_projects?: boolean;
  project_ids?: number[] | string;
  name_filter?: string;
  page_size?: number;
  page?: number;
}

interface KnowledgeBaseResponse {
  id: string | number;
  items?: KnowledgeBase[];
}

function makeNamesUnique(items: { name: string }[]) {
  const usedNames = new Map();

  return [...items]
    .sort((a, b) => a.id - b.id)
    .map((item) => {
      const originalName = item.name;

      if (!usedNames.has(originalName)) {
        usedNames.set(originalName, 0);

        return {
          ...item,
          name: originalName,
        };
      }

      let count = usedNames.get(originalName) + 1;
      let newName = `${originalName} (${count})`;

      while (usedNames.has(newName)) {
        count += 1;
        newName = `${originalName} (${count})`;
      }
      usedNames.set(originalName, count);
      usedNames.set(newName, 0);
      return {
        ...item,
        name: newName,
      };
    });
}

export const knowledgeBaseService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getKnowledgeBase: build.query<KnowledgeBaseResponse, KnowledgeBaseRequest | void>({
        query: (params) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}`,
            params: {
              ...(params || {}),
              page_size: params?.page_size || 100,
            },
          };
        },
        providesTags: ['KnowledgeBase'],
      }),
      getKnowledgeBaseById: build.query<
        KnowledgeBase,
        { id: number | string; load_projects: boolean }
      >({
        query: ({ id, load_projects = '' }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}`,
            params: {
              ...(load_projects !== '' && { load_projects }),
            },
          };
        },
        providesTags: ['KnowledgeBaseById'],
      }),
      getKnowledgeBaseFiles: build.query({
        async queryFn({ id, collector_id, sync }, _queryApi, _extraOptions, fetchWithBQ) {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
          const url = `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`;

          if (version !== API_VERSION.V2) {
            const result = await fetchWithBQ({
              url,
              params: { sync, ...(collector_id && { collector_id }) },
            });

            return result.error ? { error: result.error } : { data: result.data };
          }

          // V2 paginates files; the tree view needs the full set, so fetch every page.
          const PAGE_SIZE = 100;
          let page = 1;
          let items = [];

          while (true) {
            const result = await fetchWithBQ({
              url,
              params: { sync, page, page_size: PAGE_SIZE, ...(collector_id && { collector_id }) },
            });

            if (result.error) {
              return { error: result.error };
            }

            items = items.concat(result.data?.items || []);

            if (!result.data?.page_info?.has_next_page) {
              break;
            }

            page += 1;
          }

          return { data: items };
        },
        providesTags: ['KnowledgeBaseFiles'],
      }),
      getKnowledgeBaseFileById: build.query({
        query: ({ id, file_id }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/${file_id}`,
          };
        },
        providesTags: ['KnowledgeBaseFileById'],
      }),
      getKnowledgeBaseFileContent: build.query({
        query: ({ url }) => {
          return { url, method: 'GET' };
        },
        extraOptions: {
          responseHandler: 'text',
          mode: 'cors',
        },
        keepUnusedDataFor: 0,
        providesTags: ['KnowledgeBaseFileContent'],
      }),
      addKnowledgeBaseFile: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/file`,
            method: 'POST',
            body: body,
            formData: true,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseFiles',
                { id, sync: false },
                (draft) => {
                  draft.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add knowledge base files:', err);
          }
        },
      }),
      addKnowledgeBaseDocument: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/document`,
            method: 'POST',
            body: body,
          };
        },
        async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseFiles',
                { id, sync: false },
                (draft) => {
                  draft.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error knowledge base document:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseFiles: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseFile: build.mutation({
        query: ({ knowledgeBaseId, fileId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/files/${fileId}`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      deleteKnowledgeBaseFiles: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`,
            method: 'DELETE',
            body: body,
          };
        },
        async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
          try {
            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseFiles',
                { id, sync: false },
                (draft) => {
                  return draft.filter((file) => !body.includes(file.id));
                },
              ),
            );
          } catch (err) {
            console.error('Error deleting file:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      createKnowledgeBase: build.mutation({
        query: ({ name }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}`,
            method: 'POST',
            body: {
              name,
            },
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      deleteKnowledgeBase: build.mutation({
        query: ({ knowledge_base_id }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      editKnowledgeBaseName: build.mutation({
        query: ({ knowledge_base_id, name }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}`,
            method: 'PATCH',
            body: {
              name,
            },
          };
        },
        async onQueryStarted({ knowledge_base_id, name }, { dispatch, queryFulfilled }) {
          try {
            dispatch(
              knowledgeBaseService.util.updateQueryData('getKnowledgeBase', undefined, (draft) => {
                const currentBase = draft?.items?.find((base) => base.id === knowledge_base_id);
                currentBase.name = name;
              }),
            );
          } catch {}
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      updateKnowledgeBaseProjects: build.mutation({
        query: ({ knowledge_base_id, projects_ids }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}/projects`,
            method: 'PUT',
            body: {
              projects_ids,
            },
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }, { type: 'KnowledgeBaseById' }],
      }),
      getKnowledgeBaseCollectors: build.query({
        query: ({ id, include_data = false }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/collectors`,
            params: { include_data },
          };
        },
        transformResponse(baseQueryReturnValue: RawResultType): Promise<ResultType> | ResultType {
          return makeNamesUnique(baseQueryReturnValue);
        },
        providesTags: ['KnowledgeBaseCollectors'],
      }),
      createKnowledgeBaseCollector: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/collectors`,
            method: 'POST',
            body: body,
          };
        },
        async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
          try {
            const { data: collectors } = await queryFulfilled;
            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseCollectors',
                { id },
                (draft) => {
                  draft.collectors = draft.collectors || [];
                  draft.collectors.push(collectors);
                },
              ),
            );
          } catch (err) {
            console.error('Error create knowledge base collector:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseCollector: build.mutation({
        query: ({ knowledgeBaseId, collectorId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      deleteKnowledgeBaseCollector: build.mutation({
        query: ({ knowledgeBaseId, collectorId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
          try {
            const { data: collectors } = await queryFulfilled;
            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseCollectors',
                { id },
                (draft) => {
                  draft.collectors = draft.collectors || [];
                  draft.collectors.push(collectors);
                },
              ),
            );
          } catch (err) {
            console.error('Error delete knowledge base collector:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      setupKnowledgeBaseCollector: build.mutation({
        query: ({ knowledgeBaseId, collectorId, body }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/setup`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      runKnowledgeBaseCollector: build.mutation({
        query: ({ knowledgeBaseId, collectorId, body = {} }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/run`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      getKnowledgeBaseCollectorZendeskConsentLink: build.query({
        query: ({ knowledgeBaseId, collectorId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/${PATH_KNOWLEDGE_BASE_API.COLLECTOR_ZENDESK_OAUTH_AUTHORIZE_URL}`,
          };
        },
      }),
      getCollectorsStatus: build.query({
        query: ({ collectorId }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/collectors/${collectorId}/status`,
          };
        },
        providesTags: ['KnowledgeBaseCollectors'],
      }),
    };
  },
});

export const {
  useGetKnowledgeBaseQuery,
  useGetKnowledgeBaseByIdQuery,
  useCreateKnowledgeBaseMutation,
  useDeleteKnowledgeBaseMutation,
  useEditKnowledgeBaseNameMutation,
  useUpdateKnowledgeBaseProjectsMutation,
  useGetKnowledgeBaseFilesQuery,
  useLazyGetKnowledgeBaseFilesQuery,
  useAddKnowledgeBaseFileMutation,
  useUpdateKnowledgeBaseFilesMutation,
  useUpdateKnowledgeBaseFileMutation,
  useDeleteKnowledgeBaseFilesMutation,
  useAddKnowledgeBaseDocumentMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useCreateKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
  useDeleteKnowledgeBaseCollectorMutation,
  useSetupKnowledgeBaseCollectorMutation,
  useRunKnowledgeBaseCollectorMutation,
  useLazyGetKnowledgeBaseFileByIdQuery,
  useLazyGetKnowledgeBaseFileContentQuery,
  useLazyGetCollectorsStatusQuery,
} = knowledgeBaseService;
